<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.DIALOG.MOBILE_TOPUP_REQUESTED"
    :handler="
      (data) => {
        showMobileTopUpDialog(data);
      }
    "
  />

  <DialogComponent
    ref="modal"
    type="modal"
    mainClass="fixed z-[61]"
    @closed="
      () => {
        if (currentResolve) {
          currentResolve({ accepted: false });
          currentResolve = null;
        }
        resetDialog();
      }
    "
  >
    <div class="py-2">
      <div class="text-xl font-bold mb-5">
        {{ dialogData.title || 'Recharge mobile' }}
      </div>

      <div v-if="dialogData.message" class="mb-5 text-gray-600">
        {{ dialogData.message }}
      </div>

      <!-- Operator Info -->
      <div v-if="dialogData.operator" class="flex gap-4 mb-6 pb-6 border-b">
        <img
          v-if="dialogData.operator.logoUrls && dialogData.operator.logoUrls.length > 0"
          :src="dialogData.operator.logoUrls[1] || dialogData.operator.logoUrls[0]"
          :alt="dialogData.operator.name || dialogData.operator.operatorName"
          class="w-16 h-16 object-contain rounded-xl"
        />
        <div v-else class="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-xl text-4xl">
          {{ getOperatorEmoji(dialogData.operator.name || dialogData.operator.operatorName) }}
        </div>
        <div>
          <h3 class="font-semibold text-gray-900">{{ dialogData.operator.name || dialogData.operator.operatorName }}</h3>
          <p class="text-sm text-gray-500">{{ getOperationTypeLabel() }}</p>
        </div>
      </div>

      <!-- Phone Number Input -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">Numéro de téléphone</label>
        <div class="flex gap-2">
          <span class="py-3 px-4 bg-gray-100 rounded-xl text-gray-700 font-semibold">{{ getPhoneCode() }}</span>
          <input
            v-model="phoneNumber"
            type="tel"
            placeholder="01020304"
            class="flex-1 border-2 border-gray-200 rounded-xl p-3 focus:border-fuchsia-600 outline-none"
          />
        </div>
      </div>

      <!-- Amount Selection -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">Montant de recharge ({{ dialogData.currency || 'FCFA' }})</label>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="amount in dialogData.amountPresets"
            :key="amount"
            @click="selectedAmount = amount"
            class="py-3 px-4 rounded-xl border-2 text-center transition-all"
            :class="selectedAmount === amount ? 'border-fuchsia-800 bg-fuchsia-50' : 'border-gray-200 hover:border-fuchsia-300'"
          >
            {{ amount }}
          </button>
        </div>
        <input
          v-model.number="selectedAmount"
          type="number"
          placeholder="Autre montant"
          class="w-full mt-3 border-2 border-gray-200 rounded-xl p-3 focus:border-fuchsia-600 outline-none"
          :min="dialogData.minAmount"
          :max="dialogData.maxAmount"
        />
      </div>

      <!-- Email Input -->
      <div v-if="dialogData.requireEmail !== false" class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">Adresse e-mail (pour le reçu)</label>
        <input
          v-model="email"
          type="email"
          :placeholder="dialogData.emailPlaceholder || 'votre@email.com'"
          class="w-full border-2 border-gray-200 rounded-xl p-3 focus:border-fuchsia-600 outline-none"
        />
      </div>
    </div>

    <div class="flex gap-4 mt-5 justify-end">
      <button
        class="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
        @click="cancelDialog"
      >
        {{ dialogData.cancelText || 'Annuler' }}
      </button>
      <button
        class="px-6 py-2 rounded-lg bg-gradient-to-r from-fuchsia-800 to-fuchsia-900 text-white hover:from-fuchsia-900 hover:to-purple-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!selectedAmount || !phoneNumber || (dialogData.requireEmail !== false && !email)"
        @click="confirmDialog"
      >
        {{ dialogData.successText || 'Recharger' }}
      </button>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const dialogData = ref({
  title: '',
  message: '',
  operator: null,
  operationType: 'airtime',
  countryCode: 'BJ',
  amountPresets: [100, 200, 500, 1000, 2000, 5000],
  minAmount: 50,
  maxAmount: 100000,
  currency: 'FCFA',
  phoneCode: '+229',
  requireEmail: true,
  emailPlaceholder: 'votre@email.com',
  successText: 'Recharger',
  cancelText: 'Annuler'
});
const selectedAmount = ref(null);
const phoneNumber = ref('');
const email = ref('');
let currentResolve = null;
let currentReject = null;

const getOperatorEmoji = (name) => {
  const lower = (name || '').toLowerCase();
  if (lower.includes('mtn')) return '🟡';
  if (lower.includes('moov')) return '🔵';
  if (lower.includes('orange')) return '🟠';
  if (lower.includes('airtel')) return '🔴';
  return '📱';
};

const getOperationTypeLabel = () => {
  if (dialogData.value.operationType === 'airtime') return 'Crédit';
  if (dialogData.value.operationType === 'data') return 'Internet';
  if (dialogData.value.operationType === 'bundle') return 'Forfaits';
  return '';
};

const getPhoneCode = () => {
  return dialogData.value.phoneCode || '+229';
};

const confirmDialog = () => {
  if (currentResolve) {
    currentResolve({
      accepted: true,
      data: {
        amount: selectedAmount.value,
        phone: phoneNumber.value,
        email: email.value,
        operator: dialogData.value.operator,
        operationType: dialogData.value.operationType,
        countryCode: dialogData.value.countryCode
      }
    });
    currentResolve = null;
    currentReject = null;
  }
  resetDialog();
  proxy.$refs.modal?.hide();
};

const cancelDialog = () => {
  if (currentResolve) {
    currentResolve({ accepted: false });
    currentResolve = null;
    currentReject = null;
  }
  resetDialog();
  proxy.$refs.modal?.hide();
};

const resetDialog = () => {
  dialogData.value = {
    title: '',
    message: '',
    operator: null,
    operationType: 'airtime',
    countryCode: 'BJ',
    amountPresets: [100, 200, 500, 1000, 2000, 5000],
    minAmount: 50,
    maxAmount: 100000,
    currency: 'FCFA',
    phoneCode: '+229',
    requireEmail: true,
    emailPlaceholder: 'votre@email.com',
    successText: 'Recharger',
    cancelText: 'Annuler'
  };
  selectedAmount.value = null;
  phoneNumber.value = '';
  email.value = '';
};

const showMobileTopUpDialog = async (config) => {
  return new Promise((resolve, reject) => {
    currentResolve = resolve;
    currentReject = reject;

    dialogData.value = {
      title: config.title || 'Recharge mobile',
      message: config.message || '',
      operator: config.operator || null,
      operationType: config.operationType || 'airtime',
      countryCode: config.countryCode || 'BJ',
      amountPresets: config.amountPresets || [100, 200, 500, 1000, 2000, 5000],
      minAmount: config.minAmount || 50,
      maxAmount: config.maxAmount || 100000,
      currency: config.currency || 'FCFA',
      phoneCode: config.phoneCode || '+229',
      requireEmail: config.requireEmail !== undefined ? config.requireEmail : true,
      emailPlaceholder: config.emailPlaceholder || 'votre@email.com',
      successText: config.successText || 'Recharger',
      cancelText: config.cancelText || 'Annuler'
    };

    selectedAmount.value = config.defaultAmount || null;
    phoneNumber.value = config.defaultPhone || '';
    email.value = config.defaultEmail || '';

    proxy.$refs.modal.show();
  });
};

// Exposer la fonction globalement
onMounted(() => {
  window.mobileTopUpDialog = showMobileTopUpDialog;
});

onBeforeUnmount(() => {
  delete window.mobileTopUpDialog;
});
</script>
